import { useEffect, useRef, useState } from "react";
import { skills } from "../data/index";

export default function Skills() {
  const sectionRef = useRef(null);
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimated(true);
          }
        });
      },
      { threshold: 0.2 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const techStack = [
    { name: "React.js", icon: "⚛️" },
    { name: "JavaScript", icon: "🟨" },
    { name: "HTML5", icon: "🟧" },
    { name: "CSS3", icon: "🟦" },
    { name: "useReducer", icon: "🔁" },
    { name: "useEffect", icon: "🪝" },
    { name: "useState", icon: "📦" },
    { name: "useRef", icon: "🎯" },
    { name: "REST APIs", icon: "🌐" },
    { name: "Git", icon: "🔧" },
    { name: "Jest", icon: "🧪" },
    { name: "Vite", icon: "⚡" },
    { name: "JSON Server", icon: "🗄️" },
    { name: "MVC", icon: "🏗️" },
    { name: "Responsive Design", icon: "📱" },
    { name: "GitHub", icon: "🐙" },
  ];

  const categories = [
    {
      title: "Frontend",
      icon: "🖥️",
      color: "#22d3ee",
      items: [
        "React.js",
        "JavaScript ES6+",
        "HTML5",
        "CSS3",
        "JSX",
        "Responsive Design",
      ],
    },
    {
      title: "React Hooks",
      icon: "⚛️",
      color: "#22d3ee",
      items: [
        "useState",
        "useEffect",
        "useReducer",
        "useRef",
        "Custom Hooks",
        "useCallback",
      ],
    },
    {
      title: "Architecture",
      icon: "🏗️",
      color: "#22d3ee",
      items: [
        "Component Design",
        "Lifted State",
        "Finite State Machines",
        "MVC",
        "Inverse Data Flow",
        "Derived State",
      ],
    },
    {
      title: "Tools & Testing",
      icon: "🧪",
      color: "#22d3ee",
      items: [
        "Jest",
        "React Testing Library",
        "Git",
        "GitHub",
        "Vite",
        "Webpack",
      ],
    },
  ];

  return (
    <section
      id="skills"
      ref={sectionRef}
      style={{
        padding: "120px 24px",
        background: "#1e1e1e",
      }}
    >
      <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
        <p
          style={{
            color: "#22d3ee",
            fontSize: "0.85rem",
            fontWeight: 600,
            letterSpacing: "3px",
            textTransform: "uppercase",
            marginBottom: "12px",
          }}
        >
          Skills
        </p>

        <h2
          style={{
            fontSize: "clamp(2rem, 4vw, 2.8rem)",
            fontWeight: 800,
            color: "#f5f5f5",
            marginBottom: "16px",
            letterSpacing: "-1px",
          }}
        >
          What I bring to your team
        </h2>

        <p
          style={{
            color: "#a0a0a0",
            fontSize: "1.05rem",
            maxWidth: "560px",
            lineHeight: 1.8,
            marginBottom: "70px",
          }}
        >
          Seven years of relevant experience condensed into the skills that
          matter most, not just listed but thoroughly understood and applied in
          production.
        </p>

        {/* SKILL BARS */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "20px",
            marginBottom: "80px",
          }}
        >
          {skills.map((skill, i) => (
            <div key={skill.name}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "8px",
                }}
              >
                <span
                  style={{
                    color: "#f5f5f5",
                    fontSize: "0.9rem",
                    fontWeight: 600,
                  }}
                >
                  {skill.name}
                </span>
                <span
                  style={{
                    color: "#22d3ee",
                    fontSize: "0.85rem",
                    fontWeight: 700,
                  }}
                >
                  {skill.level}%
                </span>
              </div>
              <div
                style={{
                  height: "6px",
                  background: "#2e2e2e",
                  borderRadius: "10px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: animated ? `${skill.level}%` : "0%",
                    background: "linear-gradient(90deg, #22d3ee, #00cc44)",
                    borderRadius: "10px",
                    transition: `width 1s ease ${i * 0.08}s`,
                    boxShadow: animated
                      ? "0 0 10pxrgba(34, 211, 238),0.4)"
                      : "none",
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* CATEGORY CARDS */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "20px",
            marginBottom: "80px",
          }}
        >
          {categories.map((cat) => (
            <div
              key={cat.title}
              style={{
                background: "#242424",
                border: "1px solid #2e2e2e",
                borderRadius: "16px",
                padding: "28px 24px",
                transition: "all 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#22d3ee";
                e.currentTarget.style.transform = "translateY(-6px)";
                e.currentTarget.style.boxShadow =
                  "0 12px 40pxrgba(34, 211, 238),0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#2e2e2e";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <div style={{ fontSize: "1.8rem", marginBottom: "12px" }}>
                {cat.icon}
              </div>
              <h4
                style={{
                  color: "#f5f5f5",
                  fontWeight: 700,
                  fontSize: "1rem",
                  marginBottom: "16px",
                }}
              >
                {cat.title}
              </h4>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                {cat.items.map((item) => (
                  <div
                    key={item}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span
                      style={{
                        width: "6px",
                        height: "6px",
                        background: "#22d3ee",
                        borderRadius: "50%",
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        color: "#a0a0a0",
                        fontSize: "0.85rem",
                      }}
                    >
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* TECH PILL CLOUD */}
        <div
          style={{
            background: "#242424",
            border: "1px solid #2e2e2e",
            borderRadius: "20px",
            padding: "40px",
          }}
        >
          <h3
            style={{
              color: "#f5f5f5",
              fontWeight: 700,
              fontSize: "1.1rem",
              marginBottom: "28px",
              textAlign: "center",
            }}
          >
            Full Tech Stack
          </h3>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "12px",
              justifyContent: "center",
            }}
          >
            {techStack.map((tech) => (
              <div
                key={tech.name}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  background: "#1a1a1a",
                  border: "1px solid #2e2e2e",
                  borderRadius: "50px",
                  padding: "8px 18px",
                  fontSize: "0.85rem",
                  color: "#a0a0a0",
                  fontWeight: 500,
                  transition: "all 0.3s ease",
                  cursor: "default",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#22d3ee";
                  e.currentTarget.style.color = "#22d3ee";
                  e.currentTarget.style.boxShadow =
                    "0 0 12pxrgba(34, 211, 238),0.15)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#2e2e2e";
                  e.currentTarget.style.color = "#a0a0a0";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <span>{tech.icon}</span>
                <span>{tech.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
